/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, April 18th 2023, 10:20:07 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { Organization } from "lib/schemas";
import { PackageWidgets } from "saas-subscription/PackageWidgets";
import { SubscriptionPlan } from "saas-subscription/schemas";

interface PurchaseBodyProps {
  subscriptionPlan: SubscriptionPlan;
  selectedPrice: SubscriptionPlan["prices"][number];
  packageColor: string;
  selectedOrganization: Organization;
}

interface UseHandlePurchaseArgs {
  selectedOrganization: Organization;
  subscriptionPlan: SubscriptionPlan;
}

interface UseHandlePurchaseReturn {
  handlePurchase: (args: { selectedPrice: SubscriptionPlan["prices"][number] }) => void;
  isMakingPurchase: boolean;
  isError: boolean;
  isSuccessful: boolean;
  purchaseSuccessTitle?: React.ReactNode;
  purchaseSuccessContent?: React.ReactNode;
}

interface UsePurchaseFlowStrategyReturn {
  getWidgets: (plan: SubscriptionPlan) => typeof PackageWidgets[SubscriptionPlan["metadata"]["product_code"]];
  getPurchaseCTA: (args: { onClickPlan: () => void; subscriptionPlan: SubscriptionPlan }) => React.ReactNode;
  getPurchaseFormTitle: (plan: SubscriptionPlan) => string;
  getPurchaseFormIcon: (plan: SubscriptionPlan) => React.ReactNode;
  getPurchaseFormBody: (args: PurchaseBodyProps) => React.ReactNode;
  useHandlePurchase: (args: UseHandlePurchaseArgs) => UseHandlePurchaseReturn;
}

type UsePurchaseFlowStrategy = () => UsePurchaseFlowStrategyReturn;

export const usePurchaseFlowStrategy: UsePurchaseFlowStrategy = () => {
  return {
    getWidgets: (subscriptionPlan) => PackageWidgets[subscriptionPlan.metadata?.product_code],
    getPurchaseFormTitle: (subscriptionPlan) => `Buy ${subscriptionPlan.name}`,
    getPurchaseFormIcon: (subscriptionPlan) => <></>,
    getPurchaseFormBody: ({ selectedOrganization, packageColor, selectedPrice, subscriptionPlan }) => (
      <>You have chosen {subscriptionPlan.name}</>
    ),
    getPurchaseCTA: ({ onClickPlan }) => <></>,
    useHandlePurchase: () => ({
      isMakingPurchase: false,
      isError: false,
      isSuccessful: false,
      handlePurchase: () => {},
      selectedPrice: null,
      purchaseSuccessTitle: null,
      purchaseSuccessContent: null,
    }),
  };
};
